import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useCreateCategoryMutation, type Category } from '@/api/endpoints';
import { apiFieldErrors } from '@/api/baseApi';
import { Input, Select } from '@/components/ui';
import { useAccess } from '@/hooks/useAccess';
import { CreatableSelect } from '@/components/CreatableSelect';

const emptyCategory = { name: '', parentId: '' };

interface CategoryPickerProps {
  label?: string;
  value: string;
  onChange: (categoryId: string) => void;
  categories: Category[];
  hint?: string;
  error?: string;
  required?: boolean;
}

/** A category dropdown that can create the category it is missing. */
export function CategoryPicker({
  label = 'Category',
  value,
  onChange,
  categories,
  hint,
  error,
  required,
}: CategoryPickerProps) {
  const { can } = useAccess();
  const [createCategory, createState] = useCreateCategoryMutation();
  const [form, setForm] = useState(emptyCategory);

  const fieldErrors = apiFieldErrors(createState.error);

  return (
    <CreatableSelect
      label={label}
      noun="category"
      required={required}
      value={value}
      onChange={onChange}
      hint={hint}
      error={error}
      placeholder="No category"
      options={categories.map((category) => ({
        id: category.id,
        // Categories nest, so show the parent to tell two "Drinks" apart.
        label: category.parent ? `${category.parent.name} › ${category.name}` : category.name,
      }))}
      canCreate={can(P.CATEGORY_MANAGE)}
      creating={createState.isLoading}
      createDisabled={!form.name}
      onReset={() => setForm(emptyCategory)}
      onCreate={async () => {
        const created = await createCategory({
          name: form.name,
          parentId: form.parentId || undefined,
        }).unwrap();
        return created.id;
      }}
      form={
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Name"
            placeholder="Beverages"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            error={fieldErrors.name}
          />
          <Select
            label="Inside"
            value={form.parentId}
            onChange={(event) => setForm({ ...form, parentId: event.target.value })}
            hint="Leave blank for a top-level category."
          >
            <option value="">Top level</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </Select>
        </div>
      }
    />
  );
}
